import { useEffect, useRef, useState } from "react";
import { HERO_VIDEO_URL, HERO_VIDEO_URL_MOBILE } from "../../mock";

// Detección de mobile: ancho ≤ 768px O dispositivo con puntero táctil.
// Combina ambos para cubrir teléfonos en landscape y tablets touch.
function pickVideoSrc() {
  if (typeof window === "undefined") return HERO_VIDEO_URL;
  const isMobile = window.matchMedia(
    "(max-width: 768px), (pointer: coarse) and (max-width: 1024px)"
  ).matches;
  return isMobile ? HERO_VIDEO_URL_MOBILE : HERO_VIDEO_URL;
}

const TAIL_SECONDS = 15;
const LERP = 0.08;
const HERO_SCROLL_VH = 350;

export default function VideoBackdrop() {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [duration, setDuration] = useState(0);
  const [failed, setFailed] = useState(false);
  // Resuelto una vez al montar — la fuente del video no cambia con resize.
  const [videoSrc] = useState(pickVideoSrc);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onMeta = () => {
      setDuration(video.duration || 0);
      setReady(true);
    };
    const onError = () => setFailed(true);
    video.addEventListener("loadedmetadata", onMeta);
    video.addEventListener("error", onError);
    return () => {
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("error", onError);
    };
  }, []);

  useEffect(() => {
    if (!ready || !duration || failed) return;
    const video = videoRef.current;
    if (!video) return;

    let mode = "hero"; // 'hero' | 'tail'
    let targetTime = 0;
    let raf = 0;

    const heroEndPx = () =>
      (HERO_SCROLL_VH * window.innerHeight) / 100 - window.innerHeight;
    const tailStart = Math.max(0, duration - TAIL_SECONDS);

    const computeTarget = () => {
      const sy = window.scrollY;
      const heroEnd = heroEndPx();

      if (sy < heroEnd) {
        // HERO mode: scroll → frame 0 hasta tailStart
        const p = Math.max(0, Math.min(1, sy / heroEnd));
        targetTime = p * tailStart;

        if (mode !== "hero") {
          mode = "hero";
          video.pause();
          video.playbackRate = 1;
        }

        const sections = document.getElementById("sections-wrapper");
        if (sections) {
          sections.style.transform = "translateY(100vh)";
          sections.style.transition = "none";
        }
      } else {
        // TAIL mode: scroll controla los últimos TAIL_SECONDS Y las
        // secciones suben simultáneamente. Sin loop automático.
        if (mode !== "tail") {
          mode = "tail";
          video.pause();
          video.playbackRate = 1;
        }

        const scrollPastHero = sy - heroEnd;
        const maxScroll = window.innerHeight * 2.5;
        const phase2 = Math.max(0, Math.min(1, scrollPastHero / maxScroll));

        // Video avanza desde tailStart hasta duration
        targetTime = tailStart + phase2 * (duration - tailStart);

        // Secciones suben simultáneamente
        const sections = document.getElementById("sections-wrapper");
        if (sections) {
          const translateY = (1 - phase2) * 100;
          sections.style.transform = `translateY(${translateY}vh)`;
          sections.style.transition = "none";
        }
      }
    };

    const animate = () => {
      computeTarget();
      // LERP aplicado en AMBOS modos (hero y tail)
      const cur = video.currentTime || 0;
      const next = cur + (targetTime - cur) * LERP;
      if (Math.abs(next - cur) > 0.005) {
        try {
          video.currentTime = next;
        } catch (e) {}
      }
      raf = requestAnimationFrame(animate);
    };

    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [ready, duration, failed]);

  return (
    <div
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        background: "var(--bg)",
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      {!failed && (
        <video
          ref={videoRef}
          src={videoSrc}
          muted
          playsInline
          preload="auto"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
            opacity: ready ? 1 : 0,
            transition: "opacity 0.6s ease",
          }}
        />
      )}
    </div>
  );
}
